import { Icon16ArrowLeftOutline as BackIcon } from '@vkontakte/icons';
import classNames from 'clsx';
import React, { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    ENABLE_FULL_RESPONSIVE,
    IS_BLOCKED,
    IS_CHOSEN_PUBLIC_FILE,
    IS_FROZEN,
    IS_MY_TEAM,
    IS_PUBLIC_FOLDER,
    NEW_PORTAL_HEADER,
    PUBLIC_NOT_AVAILABLE,
    PUBLIC_NOT_EXISTS,
} from 'reactApp/appHelpers/configHelpers';
import { isViewContentOnly } from 'reactApp/appHelpers/settingsHelpers';
import { BREAKPOINT_MD } from 'reactApp/constants/breakpoints';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { useWindowSize } from 'reactApp/hooks/useWindowSize';
import { ActionPanelSelectors } from 'reactApp/modules/actionpanel/actionpanel.selectors';
import { getAllDocumentsCurrentType } from 'reactApp/modules/allDocuments/allDocuments.selectors';
import { EbookTheme } from 'reactApp/modules/ebook/ebook.types';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { getCurrentStorage, isSubscriptionPage } from 'reactApp/modules/router/router.selectors';
import { getSearchEmptyFilters } from 'reactApp/modules/search/search.selectors';
import { SettingsSelectors } from 'reactApp/modules/settings/settings.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { ThemeContext } from 'reactApp/modules/theme/theme.context';
import { EHeaderColor, HeaderWrapper } from 'reactApp/ui/Header/HeaderWrapper';
import { LeftSide } from 'reactApp/ui/Header/LeftSide';
import { Logo } from 'reactApp/ui/Header/Logo/Logo';
import { RightSide } from 'reactApp/ui/Header/RightSide';
import { tooltipPlacements } from 'reactApp/ui/Tooltip/Tooltip.constants';
import { ViewerShareButton } from 'reactApp/ui/ViewerShareButton/ViewerShareButton';

import styles from './Header.css';

interface Props {
    openedFileId?: string;

    onBackClick?(): void;

    onBookmark?(isActiveBookmark: boolean): void;

    isEbookReader?: boolean;
    theme?: EbookTheme;
}

// eslint-disable-next-line complexity, max-lines-per-function
export const Header = memo(({ openedFileId, onBackClick, isEbookReader, theme, onBookmark }: Props) => {
    const storage = useSelector(getCurrentStorage);
    const {
        isHome,
        isSearch,
        isAttaches,
        isGallery,
        isTrashBin,
        isAllDocuments,
        isPdfEdit,
        isIntegration,
        isQuotaCleaner,
        isSubscription,
        isFamilyPage,
        isEditor,
    } = getStorage(storage);
    const hideRightSide = !useSelector(getAllDocumentsCurrentType) && isAllDocuments;
    const { windowWidth } = useWindowSize();
    const isPanelOpened = useSelector(ActionPanelSelectors.isActionPanelOpened);
    const actionPanelWidth = isPanelOpened ? 380 : 0;
    const isResponsiveEnable = ENABLE_FULL_RESPONSIVE && windowWidth - actionPanelWidth <= BREAKPOINT_MD;
    const isDark = useContext(ThemeContext).isDark;

    const hideLogo = ENABLE_FULL_RESPONSIVE;

    const emptyFilters = useSelector(getSearchEmptyFilters);
    const queryParams = useSelector(SettingsSelectors.getQueryParams);
    const isPhone = useSelector(EnvironmentSelectors.isPhone);
    const hideHeader =
        (isPhone &&
            (isSubscriptionPage() ||
                isHome ||
                IS_PUBLIC_FOLDER ||
                isAttaches ||
                isGallery ||
                isTrashBin ||
                isSearch ||
                isViewContentOnly)) ||
        isQuotaCleaner ||
        (NEW_PORTAL_HEADER && !isPdfEdit && !isEbookReader && !(IS_CHOSEN_PUBLIC_FILE && isPhone)) ||
        isEditor ||
        isFamilyPage;

    const hasSearchQuery = Boolean(queryParams?.query);
    const searchOnlyExpanded = isSearch || (isHome && emptyFilters) || (isHome && hasSearchQuery) || (isAttaches && hasSearchQuery);
    const [isSearchExpanded, setSearchExpanded] = useState<boolean>(searchOnlyExpanded);

    let color = isPhone ? EHeaderColor.BLUE : EHeaderColor.WHITE;

    if (isEbookReader) {
        if (theme === EbookTheme.CEPIA) {
            color = EHeaderColor.CEPIA;
        } else if (theme === EbookTheme.DARK) {
            color = EHeaderColor.DARK;
        }
    }

    if (isSubscription || isDark) {
        color = EHeaderColor.DARK;
    }

    const toggleSearch = useCallback((toggleState: boolean) => {
        setSearchExpanded(toggleState);
    }, []);

    useEffect(() => {
        if (!searchOnlyExpanded) {
            setSearchExpanded(false);
        }
    }, [searchOnlyExpanded, storage]);

    const content = useMemo(() => {
        if (IS_FROZEN || IS_BLOCKED || PUBLIC_NOT_EXISTS || PUBLIC_NOT_AVAILABLE || isIntegration) {
            return <Logo />;
        }

        if (isPhone && IS_CHOSEN_PUBLIC_FILE) {
            return (
                <>
                    <Logo />
                    {IS_MY_TEAM && (
                        <div
                            className={styles.mobile_button_back}
                            onClick={() => {
                                location.href = `${ROOT_FOLDER_ID}${IS_MY_TEAM ? '?wv=1' : ''}`;
                            }}
                        >
                            <BackIcon width={16} height={20} fill="currentColor" />
                        </div>
                    )}
                    <ViewerShareButton basic placement={tooltipPlacements.BOTTOM_RIGHT} />
                </>
            );
        }

        return (
            <>
                <LeftSide
                    storage={storage}
                    toggleSearch={toggleSearch}
                    isSearchExpanded={isSearchExpanded}
                    isResponsiveEnable={isResponsiveEnable}
                    hideLogo={hideLogo}
                    openedFileId={openedFileId}
                    onBackClick={onBackClick}
                    color={color}
                />
                {!hideRightSide && (
                    <RightSide
                        storage={storage}
                        toggleSearch={toggleSearch}
                        isSearchExpanded={isSearchExpanded}
                        searchOnlyExpanded={searchOnlyExpanded}
                        isResponsiveEnable={isResponsiveEnable}
                        isEbookReader={isEbookReader}
                        onBookmark={onBookmark}
                        color={color}
                    />
                )}
            </>
        );
    }, [
        isIntegration,
        isPhone,
        storage,
        toggleSearch,
        isSearchExpanded,
        isResponsiveEnable,
        hideLogo,
        openedFileId,
        onBackClick,
        color,
        hideRightSide,
        searchOnlyExpanded,
        isEbookReader,
        onBookmark,
    ]);

    if (hideHeader) {
        return null;
    }

    return (
        <HeaderWrapper className={classNames({ [styles.withShadow]: !isPdfEdit && !isIntegration })} color={color} isPhone={isPhone}>
            {content}
        </HeaderWrapper>
    );
});

Header.displayName = 'Header';
